import Divider from '@mui/material/Divider';
import Grid from '@mui/material/Grid';
import Typography from '@mui/material/Typography';
import { LazyLoadable } from '@openx/components-core/lib/LazyLoadable/LazyLoadable.tsx';
import { palette } from '@openx/components-core/lib/theme/paletteV2.ts';
import { type ComponentProps, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { useDirectDealsPublisherDetails } from '../../utils/context/DirectDealsPublisherDetailsContext.tsx';
import { AudienceChartCard } from './parts/AudienceChartCard.tsx';
import { CountriesCard } from './parts/CountriesCard.tsx';
import { DemographicInfoCard } from './parts/DemographicInfoCard.tsx';
import { OverviewStatsBoxes } from './parts/OverviewStatsBoxes.tsx';
import type { StripeProgressChartBarItem } from './parts/StripePercentageChartBar.tsx';

// Deferred: pulls in the chart.js/chartjs-chart-geo/d3-geo/topojson map-rendering stack
const RegionsMapCard = LazyLoadable(() => import('./parts/RegionsMapCard.tsx'), 'RegionsMapCard');

export const AudienceAndReachSection = () => {
  const { t } = useTranslation();
  const publisherDetails = useDirectDealsPublisherDetails();

  const overviewStatsProps: ComponentProps<typeof OverviewStatsBoxes> = {
    boxOneTitle: t('Impression (Monthly Average)'),
    boxOneValue: `${publisherDetails?.mln_impressions}M`,
    ...(!!publisherDetails?.mln_unique_visitors && {
      boxTwoTitle: t('Unique Visitors (Monthly)'),
      boxTwoValue: `${publisherDetails.mln_unique_visitors}M`,
    }),
  };

  const gendersChartDataItems = useMemo((): StripeProgressChartBarItem[] => {
    if (!publisherDetails) {
      return [];
    }

    return [
      { label: t('Male'), color: palette.success.main, percentage: publisherDetails.audience_distribution_male },
      { label: t('Female'), color: palette.info.main, percentage: publisherDetails.audience_distribution_female },
      { label: t('Other'), color: palette.error.main, percentage: publisherDetails.audience_distribution_other },
    ];
  }, [publisherDetails, t]);

  if (!publisherDetails) {
    return null;
  }

  return (
    <Grid
      container
      sx={{
        padding: 4,
        borderBottom: 1,
        borderColor: 'divider',
        width: 1,
      }}
    >
      <Grid size={3}>
        <Typography data-test="audience-and-reach-section" variant="h1">
          {t('Audience & Reach')}
        </Typography>
      </Grid>

      <Grid size={6}>
        <OverviewStatsBoxes {...overviewStatsProps} />

        <AudienceChartCard title={t('Audience')} items={gendersChartDataItems} />

        <RegionsMapCard title={t('Market Region Map')} regions={publisherDetails.geo_region} />

        <CountriesCard title={t('Countries')} />

        <DemographicInfoCard title={t('Other Demographic Info')} />
      </Grid>
      <Divider />
    </Grid>
  );
};
